import React, { useState, useEffect } from 'react';
import { Search, Clipboard, Globe, Code, Sparkles } from 'lucide-react';
import DOMPurify from 'dompurify';
import { highlightCode } from '@snipit/core/utils/highlighter';
import { minifyExpression } from '@snipit/core/utils/formatters';

import { collectionGroup, query, where, onSnapshot, limit, orderBy } from 'firebase/firestore';
import { db } from '@snipit/core/services/firebase';
import { ThumbsUp, Lock } from 'lucide-react';
import { useSnippets } from '@snipit/core/hooks/useSnippets';
import { useAuth } from '@snipit/core/context/AuthContext';

export default function CodeGalleryView() {
  const { userLicense } = useAuth();
  const [snippets, setSnippets] = useState([]);
  const [activeVault, setActiveVault] = useState('dev'); // 'dev' (Code) or 'ai' (AI)
  const [queryText, setQueryText] = useState('');
  const [activeLanguage, setActiveLanguage] = useState('All');
  const { likeSnippet, fetchPublicSnippetContent } = useSnippets();
  const [expandedSnippets, setExpandedSnippets] = useState({});
  
  useEffect(() => {
    // Fetch latest public snippets with a strict limit to conserve Firebase read quota
    const q = query(
      collectionGroup(db, 'snippets_metadata'), 
      where('isPublic', '==', true),
      limit(50)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const publicSnippets = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSnippets(publicSnippets);
    });

    return () => unsubscribe();
  }, []);

  const vaultSnippets = snippets.filter(s => s.vault === activeVault);
  const availableLangs = ['All', ...Array.from(new Set(vaultSnippets.map(s => s.type)))].sort();

  const filtered = vaultSnippets.filter(s => {
    const matchesQuery = s.title.toLowerCase().includes(queryText.toLowerCase()) ||
                         s.content.toLowerCase().includes(queryText.toLowerCase());
    const matchesLang = activeLanguage === 'All' || s.type === activeLanguage;
    return matchesQuery && matchesLang;
  });

  const copyToClipboard = async (snippet) => {
    let content = expandedSnippets[snippet.id];
    if (!content) {
      const data = await fetchPublicSnippetContent(snippet.id, snippet.userId);
      content = data?.content;
    }
    if (!content) return alert("Failed to fetch snippet content.");

    const text = (snippet.type === 'AppSheet' || snippet.type === 'Sheets') ? minifyExpression(content) : content;
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy", err);
      alert("Failed to copy to clipboard.");
    }
  };

  const handleToggleExpand = async (snippet) => {
    if (expandedSnippets[snippet.id]) {
      const copy = { ...expandedSnippets };
      delete copy[snippet.id];
      setExpandedSnippets(copy);
      return;
    }

    const data = await fetchPublicSnippetContent(snippet.id, snippet.userId);
    if (data && data.content) {
      setExpandedSnippets(prev => ({ ...prev, [snippet.id]: data.content }));
    } else {
      alert("Failed to load snippet content.");
    }
  };

  const handleLike = async (snippetId, authorId) => {
    try {
      await likeSnippet(snippetId, authorId);
    } catch(err) {
      alert("Please sign in to like snippets.");
    }
  };

  return (
    <div className="w-full h-full bg-[#121212] text-gray-300 flex flex-col p-6 lg:p-8">
      <div className="flex justify-between items-center mb-6 shrink-0">
        <h1 className="text-white font-bold text-2xl flex items-center gap-2">
          <Globe className="text-blue-500" /> Public Code Gallery
        </h1>
      </div>

      {/* Sub-navigation for Code vs AI */}
      <div className="flex gap-4 mb-6 shrink-0 border-b border-gray-800 pb-2">
        <button 
          onClick={() => { setActiveVault('dev'); setActiveLanguage('All'); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${activeVault === 'dev' ? 'bg-blue-600/20 text-blue-400 font-bold border border-blue-900/50' : 'text-gray-400 hover:bg-gray-800'}`}
        >
          <Code size={18} /> Code Snippets
        </button>
        <button 
          onClick={() => { setActiveVault('ai'); setActiveLanguage('All'); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${activeVault === 'ai' ? 'bg-purple-600/20 text-purple-400 font-bold border border-purple-900/50' : 'text-gray-400 hover:bg-gray-800'}`}
        >
          <Sparkles size={18} /> AI Prompts
        </button>
      </div>

      <div className="flex gap-4 mb-6 shrink-0">
        <div className="relative grow">
          <label htmlFor="gallery-search" className="sr-only">Search Public Gallery</label>
          <Search className="absolute left-3 top-3 text-gray-500" size={16} />
          <input
            id="gallery-search"
            className="w-full bg-[#1e1e1e] p-3 pl-10 text-sm rounded-lg outline-none border border-gray-800 focus:border-gray-600 transition-colors"
            placeholder={`Search public ${activeVault === 'dev' ? 'code snippets' : 'AI prompts'}...`}
            value={queryText} onChange={e => setQueryText(e.target.value)} autoComplete="off"
          />
        </div>
        
        <select 
          className="bg-[#1e1e1e] border border-gray-800 rounded-lg p-3 text-sm outline-none text-gray-300 min-w-37.5"
          value={activeLanguage} onChange={e => setActiveLanguage(e.target.value)}
        >
          {availableLangs.map(lang => (
            <option key={lang} value={lang}>{lang}</option>
          ))}
        </select>
      </div>

      <div className="grow overflow-y-auto space-y-4 custom-scrollbar pr-2">
        {activeVault === 'ai' && userLicense?.tier === 'free' ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-6 bg-[#1a1a1a] rounded-xl border border-gray-800">
            <Lock className="w-16 h-16 text-purple-500 mb-2" />
            <h2 className="text-2xl font-bold text-white">Unlock the AI Prompts Vault</h2>
            <p className="text-gray-400 max-w-md">
              The AI Prompts Vault is a premium feature. Upgrade your license to access community AI context prompts and tools!
            </p>
            <button className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors shadow-lg shadow-purple-500/20">
              Upgrade to Premium
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-600 mt-16 text-lg">No public snippets found in this category.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map(s => (
              <div key={s.id} className="bg-[#1a1a1a] p-4 rounded-xl border border-gray-800 shadow-lg flex flex-col h-full hover:border-gray-700 transition">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex flex-col truncate pr-4">
                    <span className="text-base font-semibold text-white truncate">{s.title}</span>
                    <div className="flex gap-2 mt-2 items-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-mono ${activeVault === 'dev' ? 'bg-blue-900/30 text-blue-400' : 'bg-purple-900/30 text-purple-400'} border ${activeVault === 'dev' ? 'border-blue-900/50' : 'border-purple-900/50'}`}>
                        {s.type}
                      </span>
                      {s.tags && s.tags.length > 0 && s.tags.map(t => (
                        <span key={t} className="text-[10px] text-gray-500 font-mono">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button 
                      onClick={() => handleLike(s.id, s.userId)} 
                      className="p-2 rounded bg-[#252525] hover:bg-gray-700 text-gray-400 hover:text-white transition shadow flex items-center gap-1" 
                      title="Like Snippet"
                    >
                      <ThumbsUp size={16} />
                      <span className="text-xs font-bold">{s.likesCount || 0}</span>
                    </button>
                    <button 
                      onClick={() => copyToClipboard(s)} 
                      className="p-2 rounded bg-[#252525] hover:bg-gray-700 text-gray-400 hover:text-white transition shadow" 
                      title="Copy Code"
                    >
                      <Clipboard size={16} />
                    </button>
                  </div>
                </div>
                
                <div 
                  className="bg-black p-4 rounded overflow-auto border border-gray-900 shadow-inner custom-scrollbar min-h-15 cursor-pointer hover:border-gray-700 transition"
                  onClick={() => handleToggleExpand(s)}
                  title={expandedSnippets[s.id] ? "Click to collapse" : "Click to view content"}
                >
                  {expandedSnippets[s.id] ? (
                    s.vault === 'ai' && s.type === 'Gem' ? (
                      <pre className="m-0 text-[10px] text-gray-300 font-mono whitespace-pre-wrap wrap-break-word">
                        {expandedSnippets[s.id]}
                      </pre>
                    ) : (
                      <pre className="m-0 text-xs">
                        <code
                          className={`language-${s.type.toLowerCase()}`}
                          // eslint-disable-next-line
                          // securecoder-ignore
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightCode(expandedSnippets[s.id], s.type)) }}
                        />
                      </pre>
                    )
                  ) : (
                    <div className="text-xs text-gray-500 italic flex items-center justify-center h-full min-h-15">
                      {s.contentPreview ? `Preview: ${s.contentPreview}... (Click to expand)` : "Click to reveal snippet content..."}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
